import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ChangeDetectionStrategy, Output, EventEmitter, Inject, Input, OnDestroy } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, of, Subject, zip } from 'rxjs';
import { distinct, first, map, mergeMap, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { environment as env } from '../../../environments/environment';
import { LgvAPIService, LgvUserService } from '../../core/core.module';
import { PPC_PROPERTY_TYPE_LIST, PropertyAssociationType, PropertyType } from '../../core/enums/property.enum';
import { NotificationService } from '../../core/notifications/notification.service';
import { LgvClientService } from '../../core/services/lgv-client.service';
import { LgvPropertyService } from '../../core/services/lgv-property.service';
import { RegexConstants, RegexValidator } from '../../core/utility/lgv-regex-validator';
import { AddressValidationDialogComponent } from '../address-validation-dialog/address-validation-dialog.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'lus-add-property-modal',
    templateUrl: './add-property-modal.component.html',
    styleUrls: ['./add-property-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddPropertyModalComponent implements OnInit {

    @Output() OnOKClicked: EventEmitter<string> = new EventEmitter();

    isLoading = false;
    isLoading$ = new BehaviorSubject(this.isLoading);
    isAgentLoading$ = new BehaviorSubject(false);
    propertyInfoForm: UntypedFormGroup;
    agents$ = new BehaviorSubject([]);
    clientIdParam = '';
    validatedPropertySelected$ = new BehaviorSubject(false);
    onDestroy$ = new Subject();
    selectedProperty = {
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        county: '',
        propertyId: ''
    };
    propertyTypes$ = this.lgvPropertyService.getPropertyTypes().pipe(
        map(propertyTypes => propertyTypes
            .filter(type => PPC_PROPERTY_TYPE_LIST.includes(type.name))
            .sort((a, b) => PPC_PROPERTY_TYPE_LIST.indexOf(a.name) - PPC_PROPERTY_TYPE_LIST.indexOf(b.name))
            .map(propertyType => {
                return {
                    ...propertyType,
                    name: PropertyType[propertyType.name]
                }
            })
        ),
        tap(console.log)
    );
    selectedAddressControl = new FormControl(null);
    unitNumberControl = new FormControl('');
    zipControl = new FormControl(null);
    get isOwnedProperty(): any { return this.propertyInfoForm.get('isOwnedProperty').value === true; }
    showPrimaryPropertyToggle = true;


    constructor(
        private fb: UntypedFormBuilder,
        public lgvClientService: LgvClientService,
        public notificationService: NotificationService,
        public lgvApiService: LgvAPIService,
        public lgvUserService: LgvUserService,
        public lgvPropertyService: LgvPropertyService,
        private http: HttpClient,
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
        public MatDialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: any) { }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.clientIdParam = this.data.clientId;
            console.log('ClientID: ' + this.clientIdParam);
        });
        this.initializeForm();
    }
    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    onCancel(): void {
        this.dialogRef.close({ action: false });
    }

    initializeForm() {
        this.showPrimaryPropertyToggle = this.data?.showPrimaryPropertyToggle;
        this.propertyInfoForm = this.fb.group({
            isOwnedProperty: [true, [Validators.required]],
            primaryProperty: [false],
            propertyType: [null, []],
            listingPrice: [null, [RegexValidator(RegexConstants.GREATER_THAN_ZERO)]],
        });

        if(this.showPrimaryPropertyToggle) {
            this.propertyInfoForm.controls?.primaryProperty.setValidators(Validators.required);
        }


        // Check for changes to isOwnedProperty
        this.propertyInfoForm.get('isOwnedProperty').valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            if (this.isOwnedProperty) {
                this.propertyInfoForm.controls.listingPrice.setValidators([]);
                this.propertyInfoForm.controls.propertyType.setValidators([]);

                this.propertyInfoForm.controls.listingPrice.setValue(null);
                this.propertyInfoForm.controls.propertyType.setValue(null);

                this.propertyInfoForm.controls.listingPrice.markAsTouched();
                this.propertyInfoForm.controls.propertyType.markAsTouched();
            }
            else {
                this.propertyInfoForm.controls.listingPrice.setValidators([Validators.required]);
                this.propertyInfoForm.controls.listingPrice.markAsTouched();
                this.propertyInfoForm.controls.propertyType.setValidators([Validators.required]);
                this.propertyInfoForm.controls.propertyType.markAsTouched();
            }

        });

    }

    onSubmit() {

        if (this.propertyInfoForm.invalid) { return; }
        this.onValidateProperties();


        this.validatedPropertySelected$.pipe(
            first(f => f === true),
            takeUntil(this.onDestroy$),
            mergeMap((haveValidProperty) => {
                this.isLoading$.next(true);

                const propertyBody = {
                    address: this.selectedProperty.streetAddress,
                    city: this.selectedProperty.city,
                    state: this.selectedProperty.state,
                    zip: this.selectedProperty.zipCode,
                    county: this.selectedProperty.county,
                    primaryProperty: this.propertyInfoForm.controls['primaryProperty'].value,
                    propertyAssociationTypeId: this.propertyInfoForm.value.isOwnedProperty ? PropertyAssociationType.Owned : PropertyAssociationType.Prospect,
                    propertyTypeId: this.propertyInfoForm.value.isOwnedProperty ? undefined : this.propertyInfoForm.value.propertyType?.id,
                    listingPrice: this.propertyInfoForm.value.isOwnedProperty ? undefined : this.propertyInfoForm.value.listingPrice,
                    propertyValuationId: this.selectedProperty?.propertyId
                }

                return this.lgvPropertyService.addClientProperty(this.clientIdParam, propertyBody);

            }),
            mergeMap((propertyResponse) => {
                return zip(
                    of(propertyResponse),
                    this.lgvApiService.FetchPublicRecordReportByID(propertyResponse.id, this.clientIdParam)
                );
            })
        ).subscribe(([addPropertyResponse, publicRecordResponse]) => {

            //console.log('addPropertyResponse:');
            //console.log(addPropertyResponse);

            //console.log('publicRecordResponse:');
            //console.log(publicRecordResponse);

            this.isLoading$.next(false);

            if (addPropertyResponse && addPropertyResponse["id"]) {
                this.dialogRef.close({ action: true });
            }
            else {
                this.notificationService.error("There was an error adding the property.")
            }
        }, (err) => {
            this.isLoading$.next(false);
            this.notificationService.error('There was an error adding this property');
            console.log(err);
        });

    }


    onValidateProperties() {
        this.isLoading$.next(true);
        if (this.propertyInfoForm.invalid) { return; }
        const property = {
            address: this.selectedAddressControl.value?.address + ' ' + this.unitNumberControl?.value,
            city: this.selectedAddressControl.value?.city,
            state: this.selectedAddressControl.value?.state,
            zipCode: this.zipControl.value,
        }
        this.fetchValidatedProperties(property);
    }

    fetchValidatedProperties(param) {
        const bldUrl = `${env.LgvApiBaseUrl}/properties/validation?address=${param.address}&zip=${param.zipCode}&state=${param.state}&city=${param.city}`;
        this.http.get<any>(bldUrl, param).pipe(first()).subscribe(data => {
            if (data) {
                const dialogRef = this.dialog.open(AddressValidationDialogComponent, {
                    data,
                    height: '400px',
                    disableClose: true
                });
                dialogRef.afterClosed().subscribe((response) => {
                    if (response.action) {
                        this.selectedProperty = response.selectedProperty;
                        //this.cdr?.detectChanges();
                        this.validatedPropertySelected$.next(response.action);
                    }
                })
                this.isLoading$.next(false);
            }
        },
            (err) => {
                const dialogRef = this.dialog.open(AddressValidationDialogComponent, {
                    data: null,
                    height: '400px',
                    disableClose: true
                });
                dialogRef.afterClosed().subscribe((response) => {
                    this.validatedPropertySelected$.next(response.action);
                });
                this.isLoading$.next(false);
            }
        );
    }

}
